import type { ZippyPayConfig } from '../types';
import { resolveContainer } from '../utils';
import { MODAL_STYLES } from '../theme';

export type EmbedHost = {
  host: HTMLElement;
  overlay: HTMLElement | null;
  escapeHandler: ((event: KeyboardEvent) => void) | null;
  destroy: () => void;
};

export function createEmbedHost(config: ZippyPayConfig): EmbedHost {
  const mode = config.mode ?? 'inline';

  if (mode === 'modal') {
    return createModalHost(config);
  }

  if (!config.container) {
    throw new Error('Zippy Pay: `container` is required when mode is "inline".');
  }

  const container = resolveContainer(config.container);
  container.innerHTML = '';
  const host = document.createElement('div');
  host.className = 'zippy-inline-host';
  container.appendChild(host);

  return {
    host,
    overlay: null,
    escapeHandler: null,
    destroy: () => {
      host.remove();
    },
  };
}

function createModalHost(config: ZippyPayConfig): EmbedHost {
  const overlay = document.createElement('div');
  overlay.className = 'zippy-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Zippy Pay checkout');
  overlay.style.display = 'none';

  const style = document.createElement('style');
  style.textContent = MODAL_STYLES;
  overlay.appendChild(style);

  const panel = document.createElement('div');
  panel.className = 'zippy-modal-panel';
  const host = document.createElement('div');
  host.className = 'zippy-modal-host';
  panel.appendChild(host);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  const closeOnBackdrop = config.closeOnBackdropClick ?? false;
  if (closeOnBackdrop) {
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        config.onClose?.();
      }
    });
  }

  const embedHost: EmbedHost = {
    host,
    overlay,
    escapeHandler: null,
    destroy: () => {
      if (embedHost.escapeHandler) {
        document.removeEventListener('keydown', embedHost.escapeHandler);
      }
      overlay.remove();
      document.body.style.overflow = '';
    },
  };

  return embedHost;
}

export function showModal(embed: EmbedHost, config: ZippyPayConfig, onClose: () => void): void {
  if (!embed.overlay) {
    return;
  }
  embed.overlay.style.display = 'flex';
  document.body.style.overflow = 'hidden';

  const closeOnEscape = config.closeOnEscape ?? false;
  if (closeOnEscape && !embed.escapeHandler) {
    embed.escapeHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', embed.escapeHandler);
  }

  const focusable = embed.host.querySelector<HTMLElement>('input, button');
  focusable?.focus();
}

export function hideModal(embed: EmbedHost): void {
  if (!embed.overlay) {
    return;
  }
  embed.overlay.style.display = 'none';
  document.body.style.overflow = '';
  if (embed.escapeHandler) {
    document.removeEventListener('keydown', embed.escapeHandler);
    embed.escapeHandler = null;
  }
}
